"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { SDK } from "@/@sdk";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import { selectIntegrationConfig_Stripe } from "@/store/modules/workspace/workspace.selectors";
import { ExpressWizardShell } from "../shared/express-wizard-shell";
import { cn } from "@/lib/utils";
import {
  BrandBasicsStep,
  BrandDetailsStep,
  BrandEmailTemplatesStep,
  BrandQuoteExperienceStep,
  BrandSummaryStep,
} from "./steps/brand-wizard-steps";
import {
  BrandTrustPilotStep,
  BrandDomainsEmailStep,
  BrandStripeBrandStep,
} from "./steps/brand-polish-steps";
import {
  buildBrandWizardSteps,
  parseBrandResumeStep,
  type BrandWizardStepId,
} from "./brand-onboarding.helpers";

export type { BrandWizardStepId };

type ExpressBrandOnboardingWizardProps = {
  open?: boolean;
  embedded?: boolean;
  brandId?: string;
  singleBrandMode?: boolean;
  prefillFromWorkspace?: boolean;
  initialStepId?: BrandWizardStepId;
  onComplete: (brandId: string) => void;
  onCancel?: () => void;
};

export function ExpressBrandOnboardingWizard({
  open = true,
  embedded = false,
  brandId: initialBrandId,
  singleBrandMode = false,
  prefillFromWorkspace = false,
  initialStepId,
  onComplete,
  onCancel,
}: ExpressBrandOnboardingWizardProps) {
  const integrationConfig = useSelector(selectIntegrationConfig_Stripe);
  const stripeConnected = Boolean(integrationConfig?.auth?.apiKey);

  const wizardSteps = useMemo(
    () => buildBrandWizardSteps(stripeConnected),
    [stripeConnected]
  );

  const resolvedInitialStep = parseBrandResumeStep(
    initialStepId,
    stripeConnected
  );

  const [currentStepId, setCurrentStepId] =
    useState<BrandWizardStepId>(resolvedInitialStep);
  const [brandId, setBrandId] = useState<string | undefined>(initialBrandId);

  useEffect(() => {
    if (!open) return;
    setBrandId(initialBrandId);
    setCurrentStepId(parseBrandResumeStep(initialStepId, stripeConnected));
  }, [open, initialBrandId, initialStepId, stripeConnected]);

  const { doAction: patchOnboarding } = useSDKActionWithDeps(
    () => ({
      action: (sdk) =>
        (body: {
          brandsOnboardingCompleted?: string[];
          brandWizardProgress?: Record<
            string,
            { lastStepId?: string; completed?: boolean; completedAt?: number }
          >;
        }) => sdk.patchOrganizationOnboarding(body),
    }),
    []
  );

  const persistBrandProgress = useCallback(
    async (
      targetBrandId: string,
      progress: {
        lastStepId?: string;
        completed?: boolean;
        completedAt?: number;
      }
    ) => {
      await patchOnboarding({
        brandWizardProgress: {
          [targetBrandId]: progress,
        },
      });
    },
    [patchOnboarding]
  );

  const goToStep = useCallback(
    (stepId: BrandWizardStepId) => {
      setCurrentStepId(stepId);
      if (brandId) {
        void persistBrandProgress(brandId, { lastStepId: stepId });
      }
    },
    [brandId, persistBrandProgress]
  );

  const handleBasicsSaved = useCallback(
    (savedBrandId: string) => {
      setBrandId(savedBrandId);
      void persistBrandProgress(savedBrandId, { lastStepId: "brand-details" });
      goToStep("brand-details");
    },
    [goToStep, persistBrandProgress]
  );

  const handleComplete = useCallback(async () => {
    if (!brandId) return;

    const status = await SDK.getOrganizationOnboardingStatus();
    const existing = status?.brandsOnboardingCompleted ?? [];
    const nextIds = [...new Set([...existing, brandId])];

    await patchOnboarding({
      brandsOnboardingCompleted: nextIds,
      brandWizardProgress: {
        [brandId]: {
          completed: true,
          completedAt: Date.now(),
          lastStepId: "brand-summary",
        },
      },
    });

    onComplete(brandId);
  }, [brandId, onComplete, patchOnboarding]);

  const stepIndex = wizardSteps.findIndex((step) => step.id === currentStepId);

  const stepTitle =
    currentStepId === "brand-basics"
      ? "Brand identity"
      : currentStepId === "brand-details"
        ? "Legal & address"
        : currentStepId === "quote-experience"
          ? "Quote appearance"
          : currentStepId === "trustpilot"
            ? "TrustPilot reviews"
            : currentStepId === "domains-email"
              ? "Domains & email"
              : currentStepId === "stripe-brand"
                ? "Stripe for this brand"
                : currentStepId === "email-templates"
                  ? "Review booking emails"
                  : "Brand ready";

  const stepDescription =
    currentStepId === "brand-basics"
      ? "Name, logo, and core brand settings."
      : currentStepId === "brand-details"
        ? "Legal name, ATOL, and postal address for receipts."
        : currentStepId === "quote-experience"
          ? "Theme and styling for customer quotes."
          : currentStepId === "trustpilot"
            ? "Optional TrustPilot widget on quotes."
            : currentStepId === "domains-email"
              ? "Custom quote domain and email delivery."
              : currentStepId === "stripe-brand"
                ? "Map Stripe payments to a customer payment method."
                : currentStepId === "email-templates"
                  ? "Preview default booking email templates."
                  : undefined;

  const isQuoteExperienceStep = currentStepId === "quote-experience";

  const content = (
    <div
      className={cn(
        "space-y-4",
        isQuoteExperienceStep && "flex min-h-0 flex-1 flex-col"
      )}
    >
      {embedded ? (
        <div className="flex items-center gap-1 overflow-x-auto pb-1">
          {wizardSteps.map((step, index) => {
            const isActive = step.id === currentStepId;
            const isComplete = index < stepIndex;
            return (
              <div key={step.id} className="flex shrink-0 items-center gap-1">
                <div
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : isComplete
                        ? "bg-primary/20 text-primary"
                        : "bg-muted text-muted-foreground"
                  }`}
                >
                  {index + 1}
                </div>
                <span
                  className={`hidden truncate text-xs sm:inline ${
                    isActive ? "font-medium" : "text-muted-foreground"
                  }`}
                >
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}

      {currentStepId === "brand-basics" ? (
        <BrandBasicsStep
          brandId={brandId}
          singleBrandMode={singleBrandMode && !brandId}
          prefillFromWorkspace={prefillFromWorkspace || singleBrandMode}
          onSaved={handleBasicsSaved}
        />
      ) : null}

      {currentStepId === "brand-details" && brandId ? (
        <BrandDetailsStep
          brandId={brandId}
          onSaved={() => goToStep("quote-experience")}
        />
      ) : null}

      {currentStepId === "quote-experience" && brandId ? (
        <BrandQuoteExperienceStep
          brandId={brandId}
          onSaved={() => goToStep("trustpilot")}
          onSkip={() => goToStep("trustpilot")}
        />
      ) : null}

      {currentStepId === "trustpilot" && brandId ? (
        <BrandTrustPilotStep
          brandId={brandId}
          onContinue={() => goToStep("domains-email")}
          onSkip={() => goToStep("domains-email")}
        />
      ) : null}

      {currentStepId === "domains-email" && brandId ? (
        <BrandDomainsEmailStep
          brandId={brandId}
          onContinue={() =>
            goToStep(stripeConnected ? "stripe-brand" : "email-templates")
          }
          onSkip={() =>
            goToStep(stripeConnected ? "stripe-brand" : "email-templates")
          }
        />
      ) : null}

      {currentStepId === "stripe-brand" && brandId ? (
        <BrandStripeBrandStep
          brandId={brandId}
          onContinue={() => goToStep("email-templates")}
          onSkip={() => goToStep("email-templates")}
        />
      ) : null}

      {currentStepId === "email-templates" && brandId ? (
        <BrandEmailTemplatesStep
          brandId={brandId}
          onContinue={() => goToStep("brand-summary")}
        />
      ) : null}

      {currentStepId === "brand-summary" && brandId ? (
        <BrandSummaryStep
          brandId={brandId}
          onComplete={() => void handleComplete()}
        />
      ) : null}

      {embedded && onCancel && currentStepId === "brand-basics" ? (
        <div className="flex justify-start border-t pt-2">
          <button
            type="button"
            className="text-sm text-muted-foreground underline-offset-4 hover:underline"
            onClick={onCancel}
          >
            Back to brand list
          </button>
        </div>
      ) : null}
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <ExpressWizardShell
      open={open}
      title={stepTitle}
      description={stepDescription}
      steps={wizardSteps}
      currentStepId={currentStepId}
      blockDismiss={false}
      showBack={stepIndex > 0 && currentStepId !== "brand-summary"}
      isLastStep={currentStepId === "brand-summary"}
      hidePrimaryAction
      hideFooter
      onBack={() => {
        const prev = wizardSteps[stepIndex - 1];
        if (prev) goToStep(prev.id);
      }}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel?.();
      }}
      dialogContentClassName={cn(
        isQuoteExperienceStep
          ? "max-h-[92vh] w-[96vw] max-w-[min(96vw,1400px)]"
          : "max-w-4xl"
      )}
    >
      {content}
    </ExpressWizardShell>
  );
}
